import { Flex, Text } from '@backstage/ui';
import type {
  HistoryDays,
  TeamHistoryCheckDto,
} from '@internal/scorecards-common';
import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import { useCallback, useMemo, useState } from 'react';
import { type ColumnDef, LeveledTable } from '../LeveledTable/LeveledTable';
import { DailyBar } from './DailyBar';
import { ExpandedPanel } from './ExpandedPanel';
import styles from './HistoryChecksTable.module.css';

interface HistoryChecksTableProps {
  checks: TeamHistoryCheckDto[];
  team: string;
  days: HistoryDays;
}

export const HistoryChecksTable = ({
  checks,
  team,
  days,
}: HistoryChecksTableProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const columns = useMemo<ColumnDef<TeamHistoryCheckDto>[]>(
    () => [
      {
        id: 'name',
        label: 'Check',
        width: '1fr',
        isRowHeader: true,
        allowsSorting: true,
        renderCell: check => (
          <Flex direction="column" gap="1">
            <Text as="span" variant="body-medium">
              {check.name}
            </Text>
            {check.trackName && (
              <Text as="span" variant="body-small" color="secondary">
                {check.trackName}
              </Text>
            )}
          </Flex>
        ),
      },
      {
        id: 'daily',
        label: 'Daily',
        minWidth: 220,
        maxWidth: 460,
        renderCell: check => (
          <DailyBar daily={check.daily} severity={check.severity} />
        ),
      },
      {
        id: 'failing',
        label: 'Failing',
        width: 140,
        allowsSorting: true,
        renderCell: check => (
          <Text as="span" variant="body-medium">
            {check.failingCount} of {check.inScopeCount} Projects
          </Text>
        ),
      },
      {
        id: 'expand',
        label: '',
        width: 36,
        renderCell: check => {
          const isExpanded = expandedId === check.checkId;
          return (
            <button
              type="button"
              className={styles.expandButton}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded ? `Collapse ${check.name}` : `Expand ${check.name}`
              }
              onClick={() => setExpandedId(isExpanded ? null : check.checkId)}
            >
              {isExpanded ? (
                <RiArrowUpSLine size={16} />
              ) : (
                <RiArrowDownSLine size={16} />
              )}
            </button>
          );
        },
      },
    ],
    [expandedId],
  );

  const compare = useCallback(
    (a: TeamHistoryCheckDto, b: TeamHistoryCheckDto, column: string) => {
      if (column === 'name') return a.name.localeCompare(b.name);
      if (column === 'failing') return a.failingCount - b.failingCount;
      return 0;
    },
    [],
  );

  return (
    <LeveledTable
      items={checks}
      getLevelId={c => c.levelId}
      getItemKey={c => c.checkId}
      columns={columns}
      compare={compare}
      defaultSort={{ column: 'failing', direction: 'descending' }}
      expandedKey={expandedId}
      renderExpandedRow={check => (
        <ExpandedPanel checkId={check.checkId} team={team} days={days} />
      )}
      emptyState={
        <Text as="p" color="secondary" variant="body-small">
          No failing checks in this period.
        </Text>
      }
    />
  );
};
